/**
 * Label Operations - Funktioner för att hantera labels på kartan
 * 
 * Använder closure för att hålla state för overlays.
 * Cache keys styr om overlays behöver byggas om vid data-ändringar.
 * clearOverlayDom() tar bort DOM utan att invalidera cachen (för pan/zoom).
 */

import Overlay from '../ui/overlay';
import { featureCollectionToTurfPolygons } from '../utils/createTurfPolygons';

// State för overlays (closure)
let overlayLabels = null;
let salesDistrictsOverlay = null;

// Cache keys to skip redundant overlay rebuilds
let lastSelectionLabelKey = null;
let lastSalesDistrictLabelKey = null;

function buildSelectionCacheKey(selectionType, turfPolygons) {
    if (selectionType === 'clear' || !turfPolygons || !turfPolygons[selectionType]) {
        return 'clear';
    }
    return `${selectionType}:${turfPolygons[selectionType].length}`;
}

function buildSalesDistrictCacheKey(isSalesDistricts, salesDistricts) {
    if (!isSalesDistricts || !salesDistricts || !salesDistricts.features || salesDistricts.features.length === 0) {
        return 'none';
    }
    const parts = salesDistricts.features.map(f => {
        const id = f.id ?? f.properties?.id ?? '';
        const name = f.properties?.name ?? '';
        const color = f.properties?.color ?? '';
        return `${id}:${name}:${color}`;
    }).join(',');
    return `sd:${salesDistricts.features.length}:${parts}`;
}

/**
 * Remove overlay DOM elements without invalidating cache keys.
 * Used by zoom_changed / idle so the overlay is repositioned on next handleLabels
 * without the expensive cost of rebuilding all label data from scratch.
 */
export function clearOverlayDom() {
    if (overlayLabels) {
        overlayLabels.remove();
        overlayLabels = null;
    }
    if (salesDistrictsOverlay) {
        try {
            salesDistrictsOverlay.setMap(null);
            if (salesDistrictsOverlay.remove && typeof salesDistrictsOverlay.remove === 'function') {
                salesDistrictsOverlay.remove();
            }
        } catch (_) { /* ignore */ }
        salesDistrictsOverlay = null;
    }
}

/**
 * Hantera labels på kartan
 * @param {Object} params - Label parameters
 * @param {Object} params.map - Google Maps map instance
 * @param {Object} params.maps - Google Maps API instance
 * @param {Object} params.turfPolygons - Turf polygons object
 * @param {string} params.selectionType - Current selection type
 * @param {boolean} params.isSalesDistricts - Whether sales districts are visible
 * @param {Object} params.salesDistricts - Sales districts FeatureCollection
 */
export function handleLabels({ map, maps, turfPolygons, selectionType, isSalesDistricts, salesDistricts }) {
    const selectionKey = buildSelectionCacheKey(selectionType, turfPolygons);
    const selectionChanged = selectionKey !== lastSelectionLabelKey;
    const needsSelectionOverlay = selectionType !== 'clear' && turfPolygons[selectionType];

    if (selectionChanged || (!overlayLabels && needsSelectionOverlay)) {
        lastSelectionLabelKey = selectionKey;

        if (overlayLabels) {
            overlayLabels.remove();
            overlayLabels = null;
        }

        if (needsSelectionOverlay) {
            overlayLabels = Overlay({ 
                polygons: turfPolygons[selectionType], 
                map, 
                maps
            });
        }
    }

    if (selectionKey === 'clear' && overlayLabels) {
        removeSelectionLabels();
    }

    const sdKey = buildSalesDistrictCacheKey(isSalesDistricts, salesDistricts);
    const sdChanged = sdKey !== lastSalesDistrictLabelKey;
    const needsSdOverlay = isSalesDistricts && 
                           salesDistricts && 
                           salesDistricts.features && 
                           salesDistricts.features.length > 0;

    if (sdChanged || (!salesDistrictsOverlay && needsSdOverlay)) {
        lastSalesDistrictLabelKey = sdKey;

        if (salesDistrictsOverlay) {
            try {
                salesDistrictsOverlay.setMap(null);
                if (salesDistrictsOverlay.remove && typeof salesDistrictsOverlay.remove === 'function') {
                    salesDistrictsOverlay.remove();
                }
            } catch (_) { /* ignore */ }
            salesDistrictsOverlay = null;
        }

        if (needsSdOverlay) {
            salesDistrictsOverlay = Overlay({ 
                polygons: featureCollectionToTurfPolygons(salesDistricts), 
                map, 
                maps,
                ignoreArea: true,
                modifiers: {
                    'overlay-label--districts': true
                }
            });
        }
    }
}

/**
 * Ta bort urvalslager-labels (behåller säljdistrikt-labels).
 * Invalidates the cache key so next handleLabels will rebuild.
 */
export function removeSelectionLabels() {
    if (overlayLabels) {
        overlayLabels.remove();
        overlayLabels = null;
    }
    lastSelectionLabelKey = null;
}

/**
 * Ta bort alla labels (både urvalslager och säljdistrikt).
 * Invalidates all cache keys so next handleLabels will rebuild.
 */
export function removeAllLabels() {
    removeSelectionLabels();
    if (salesDistrictsOverlay) {
        salesDistrictsOverlay.setMap(null);
        salesDistrictsOverlay = null;
    }
    lastSalesDistrictLabelKey = null;
}

/** 
 * Hämta overlay labels (för debugging)
 * @returns {Object|null} Overlay labels instance
 */
export function getOverlayLabels() {
    return overlayLabels;
}
